import { runInAction } from 'mobx'
import axiosService from '../../../../../utils/AxiosService'
import CampaignScenarioActionGroup from '../../../../../models/CampaignScenarioActionGroup'
import { toastError, toastInfo, toastSuccess } from '../../../../FormComponents/Toasts'

const LIKED_OR_PINNED_SELECTION_STATUSES = new Set(['selected', 'backuped'])

function actionGroupsForInfluencer(campaignScenario, influencerId, slotNumber) {
  const iid = Number(influencerId)
  const groups = (campaignScenario?.campaign_scenario_action_groups || []).filter(ag => Number(ag.influencer_id) === iid)
  if (slotNumber == null) return groups
  const slot = Number(slotNumber) || 1
  return groups.filter(ag => (Number(ag.slot_number) || 1) === slot)
}

function campaignAuthenticationsForInfluencer(influencer, authentications) {
  if (!influencer) return []
  const authIds = new Set((influencer.authentications || []).map(a => Number(a.id)))
  return (authentications || []).filter(ca => authIds.has(Number(ca.authentication_id)))
}

/** Whether the client has liked (thumbs up) or pinned (backup) this influencer - the default-visibility signal. */
export function isInfluencerLikedOrPinned(influencer, authentications) {
  const cas = campaignAuthenticationsForInfluencer(influencer, authentications)
  return cas.some(ca => LIKED_OR_PINNED_SELECTION_STATUSES.has(ca.selection_status))
}

export function budgetVisibleScenarioActions(campaignScenario, context = {}) {
  const { influencers, campaign, authentications } = context
  if (!campaignScenario?.actions) return []

  return campaignScenario.actions.filter(action => {
    if (action.visible_in_budget === false) return false

    const actionGroup = (campaignScenario.campaign_scenario_action_groups || []).find(
      ag => ag.id === action.campaign_scenario_action_group_id
    )
    if (actionGroup && !actionGroup.visible_in_budget) return false

    if (influencers?.length && campaign && authentications) {
      const influencer = influencers.find(inf => Number(inf.id) === Number(action.influencer_id))
      if (
        influencer &&
        !isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, action.slot_number)
      ) {
        return false
      }
    }

    return true
  })
}

/**
 * Whether the influencer's row (slotNumber, default 1) appears in budget tables (overview, reels-like, etc.).
 * With action groups for that slot: any group `visible_in_budget`. Without groups: liked/pinned default
 * (only meaningful for slot 1 - duplicate slots always get a placeholder group on creation).
 */
export function isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber = 1) {
  if (!campaignScenario || !influencer) return true
  const groups = actionGroupsForInfluencer(campaignScenario, influencer.id, slotNumber)
  if (groups.length > 0) {
    return groups.some(ag => ag.visible_in_budget)
  }
  return isInfluencerLikedOrPinned(influencer, authentications)
}

export async function setInfluencerVisibleInBudgetForScenario({
  campaignId,
  scenarioId,
  campaignScenario,
  influencer,
  slotNumber = 1,
  newValue,
  campaignState,
}) {
  const influencerId = influencer.id
  const influencerActionGroups = actionGroupsForInfluencer(campaignScenario, influencerId, slotNumber)

  const createDefaultActionGroup = async visibleInBudget => {
    const actionGroup = await axiosService.post(
      `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups`,
      {
        campaign_scenario_action_group: {
          influencer_id: influencerId,
          action_type: 'default_visibility',
          visible_in_budget: visibleInBudget,
          slot_number: slotNumber,
        },
      }
    )
    runInAction(() => {
      campaignScenario.campaign_scenario_action_groups.push(new CampaignScenarioActionGroup(actionGroup))
      if (campaignState) {
        campaignState.campaignScenarios = [...campaignState.campaignScenarios]
        campaignState.influencers = [...campaignState.influencers]
      }
    })
    newValue ? toastSuccess('Influencer will be applied to the budget') : toastInfo('Influencer will not be applied to the budget')
  }

  if (influencerActionGroups.length === 0) {
    await createDefaultActionGroup(newValue)
    return
  }

  runInAction(() => {
    influencerActionGroups.forEach(ag => {
      ag.visible_in_budget = newValue
    })
  })

  try {
    const updatedActionGroups = await Promise.all(
      influencerActionGroups.map(actionGroup =>
        axiosService.patch(
          `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${scenarioId}/campaign_scenario_action_groups/${actionGroup.id}`,
          { campaign_scenario_action_group: { visible_in_budget: newValue } }
        )
      )
    )
    runInAction(() => {
      updatedActionGroups.forEach(updatedActionGroup => {
        const actionGroup = campaignScenario.campaign_scenario_action_groups.find(ag => ag.id === updatedActionGroup.id)
        if (actionGroup) {
          actionGroup.visible_in_budget = updatedActionGroup.visible_in_budget
          const slot = Number(slotNumber) || 1
          const updatedActions = campaignScenario.actions.filter(
            action => Number(action.influencer_id) === Number(influencerId) && (Number(action.slot_number) || 1) === slot
          )
          updatedActions.forEach(action => {
            action.visible_in_budget = updatedActionGroup.visible_in_budget
          })
        }
      })
      if (campaignState) {
        campaignState.campaignScenarios = [...campaignState.campaignScenarios]
        campaignState.influencers = [...campaignState.influencers]
      }
    })
    newValue ? toastSuccess('Influencer will be applied to the budget') : toastInfo('Influencer will not be applied to the budget')
  } catch (err) {
    runInAction(() => {
      influencerActionGroups.forEach(ag => {
        ag.visible_in_budget = !newValue
      })
    })
    const msg = err?.data?.error ?? err?.message ?? 'Could not save budget visibility'
    toastError(typeof msg === 'string' ? msg : 'Could not save budget visibility')
    throw err
  }
}
